import React, { FC } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CustomBadge } from "../../../shared/CustomBadge/CustomBadge";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "../ProfilingProtocolDetailPage.module.scss";

interface IFVProfileChartProps {
  outputs: Record<string, any>;
}

const TICK_STYLE = {
  fill: "var(--c-text-1)",
  fontSize: 11,
  fontFamily: "'Satoshi', sans-serif",
};

/** Verdetto FVimb → badge color (design system). */
function fvimbBadge(fvimb: number): { label: string; color: "lightgreen" | "lightred" } {
  if (Math.abs(fvimb) < 10) return { label: `FVimb ${fvimb}% · bilanciato`, color: "lightgreen" };
  return fvimb > 0
    ? { label: `FVimb +${fvimb}% · deficit di velocità`, color: "lightred" }
    : { label: `FVimb ${fvimb}% · deficit di forza`, color: "lightred" };
}

/**
 * Profilo F-V (Layout A — spec sez.4.1): retta forza-velocità dell'atleta + retta
 * OTTIMALE tratteggiata (stesso Pmax) + etichetta FVimb. Per i profili salto/sprint.
 * Retta atleta: F = F0·(1 − V/V0). Ottimale da Pmax e optimalSfv:
 *   F0_opt = √(4·Pmax·|Sopt|), V0_opt = √(4·Pmax/|Sopt|).
 */
export const FVProfileChart: FC<IFVProfileChartProps> = ({ outputs }) => {
  const F0 = Number(outputs?.F0);
  const V0 = Number(outputs?.V0);
  const Pmax = Number(outputs?.Pmax);
  const sOpt = Number(outputs?.optimalSfv);
  const fvimb = outputs?.FVimb;

  if (!Number.isFinite(F0) || !Number.isFinite(V0) || V0 <= 0) return null;

  const absS = Number.isFinite(sOpt) ? Math.abs(sOpt) : 0;
  const hasOptimal = absS > 0 && Number.isFinite(Pmax) && Pmax > 0;
  const F0o = hasOptimal ? Math.sqrt(4 * Pmax * absS) : F0;
  const V0o = hasOptimal ? Math.sqrt((4 * Pmax) / absS) : V0;

  const vMax = Math.max(V0, V0o) * 1.05;
  const N = 24;
  const data = Array.from({ length: N + 1 }, (_, i) => {
    const v = (vMax * i) / N;
    return {
      v: Math.round(v * 100) / 100,
      athlete: v <= V0 ? Math.round(F0 * (1 - v / V0)) : null,
      optimal: hasOptimal && v <= V0o ? Math.round(F0o * (1 - v / V0o)) : null,
    };
  });

  const badge = typeof fvimb === "number" ? fvimbBadge(fvimb) : null;

  // Punti F-V misurati (overlay scatter) — dal backend computeSJSamozino.fvPoints
  const measured: Array<{ v: number; measured: number }> = Array.isArray(
    outputs?.fvPoints,
  )
    ? outputs.fvPoints
        .filter((p: any) => Number.isFinite(p?.velocity) && Number.isFinite(p?.force))
        .map((p: any) => ({ v: Math.round(p.velocity * 100) / 100, measured: p.force }))
    : [];

  return (
    <div className={style.card}>
      <div className={style.cardHeader}>
        <Typhography size="bold-16" style={{ color: "var(--c-text-0)", margin: 0 }}>
          Profilo Forza-Velocità
        </Typhography>
      </div>

      <div className={style.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 20, right: 30, bottom: 40, left: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--c-divider)" vertical={false} />
            <XAxis
              type="number"
              dataKey="v"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Velocità (m/s)",
                position: "insideBottom",
                offset: -20,
                fill: "var(--c-text-1)",
                fontSize: 11,
                fontFamily: "'Satoshi', sans-serif",
              }}
            />
            <YAxis
              type="number"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Forza (N)",
                angle: -90,
                position: "insideLeft",
                fill: "var(--c-text-1)",
                fontSize: 11,
                fontFamily: "'Satoshi', sans-serif",
              }}
            />
            <Tooltip
              contentStyle={{
                background: "var(--c-card-bg)",
                border: "1px solid var(--c-card-border)",
                borderRadius: 8,
                fontFamily: "'Satoshi', sans-serif",
                fontSize: 12,
              }}
            />
            <Legend wrapperStyle={{ fontFamily: "'Satoshi', sans-serif", fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="athlete"
              name="Profilo atleta"
              stroke="var(--c-navy-1)"
              strokeWidth={2.5}
              dot={false}
              connectNulls
            />
            {hasOptimal && (
              <Line
                type="monotone"
                dataKey="optimal"
                name="Profilo ottimale"
                stroke="var(--c-warning)"
                strokeWidth={2}
                strokeDasharray="6 5"
                dot={false}
                connectNulls
              />
            )}
            {measured.length > 0 && (
              <Scatter
                name="Punti misurati"
                data={measured}
                dataKey="measured"
                fill="var(--c-navy-1)"
                stroke="#fff"
                strokeWidth={1.5}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className={style.chartFooter}>
        <CustomBadge
          label={`F0 ${Math.round(F0)} N · V0 ${V0} m/s · Pmax ${Math.round(Pmax)} W`}
          color="light-blue-dust"
        />
        {badge && <CustomBadge label={badge.label} color={badge.color} />}
      </div>
    </div>
  );
};

export default FVProfileChart;
